import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Badge,
} from '@mui/material';
import { ArrowBack, Send, InfoOutlined } from '@mui/icons-material';
import { sendMessage } from '../config/chat';
import { ConversationWithUser, Message } from '../types';
import { isOnline, formatDateSeparator } from '../utils/format';
import { onlineBadgeSx, borderlessInputSx } from '../utils/styles';
import ChatEmptyState from './ChatEmptyState';
import MessageBubble from './MessageBubble';

interface ChatWindowProps {
  conversation: ConversationWithUser | null;
  messages: Message[];
  currentUserId: string;
  onBack?: () => void;
  onShowContactInfo: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  conversation,
  messages,
  currentUserId,
  onBack,
  onShowContactInfo,
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || !conversation) return;
    const msg = text.trim();
    setText('');
    setSending(true);
    try {
      await sendMessage(conversation.id, currentUserId, msg);
    } catch (error) {
      console.error('Failed to send message:', error);
      setText(msg);
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) return <ChatEmptyState />;

  const userIsOnline = isOnline(conversation.otherUser.lastSeen);
  let lastDate = '';

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'rgba(14,14,26,0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          minHeight: 64,
        }}
      >
        {onBack && (
          <IconButton onClick={onBack} edge="start" size="small" sx={{ color: '#8888a8' }}>
            <ArrowBack />
          </IconButton>
        )}
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          invisible={!userIsOnline}
          sx={onlineBadgeSx}
        >
          <Avatar
            src={conversation.otherUser.avatar}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #5c35cc, #7c4dff)',
            }}
            onClick={onShowContactInfo}
          >
            {conversation.otherUser.name?.[0]?.toUpperCase()}
          </Avatar>
        </Badge>
        <Box sx={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={onShowContactInfo}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.2, color: '#e8e8f0' }}>
            {conversation.otherUser.name || conversation.otherUser.username}
          </Typography>
          <Typography variant="caption" sx={{ color: userIsOnline ? '#00e676' : '#5555a0' }}>
            {userIsOnline ? 'Online' : 'Offline'}
          </Typography>
        </Box>
        <IconButton onClick={onShowContactInfo} sx={{ color: '#8888a8' }}>
          <InfoOutlined />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          background:
            'radial-gradient(ellipse at 20% 80%, rgba(124,77,255,0.04) 0%, transparent 50%), ' +
            'radial-gradient(ellipse at 80% 20%, rgba(255,64,129,0.03) 0%, transparent 50%), ' +
            '#0a0a14',
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%237c4dff\' fill-opacity=\'0.015\'%3E%3Cpath d=\'M20 0L40 20L20 40L0 20z\'/%3E%3C/g%3E%3C/svg%3E"), ' +
            'radial-gradient(ellipse at 20% 80%, rgba(124,77,255,0.04) 0%, transparent 50%), ' +
            'radial-gradient(ellipse at 80% 20%, rgba(255,64,129,0.03) 0%, transparent 50%), ' +
            '#0a0a14',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography
              variant="body2"
              sx={{
                bgcolor: 'rgba(124,77,255,0.08)',
                display: 'inline-block',
                px: 3,
                py: 1,
                borderRadius: 3,
                color: '#8888a8',
                border: '1px solid rgba(124,77,255,0.1)',
              }}
            >
              No messages yet. Say hello!
            </Typography>
          </Box>
        )}

        {messages.map((msg) => {
          const currentDate = formatDateSeparator(msg.createdAt);
          const showDate = currentDate !== lastDate;
          if (showDate) lastDate = currentDate;

          return (
            <React.Fragment key={msg.id}>
              {showDate && (
                <Box sx={{ textAlign: 'center', my: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'rgba(124,77,255,0.08)',
                      px: 2,
                      py: 0.5,
                      borderRadius: 3,
                      color: '#7c4dff',
                      fontWeight: 500,
                      border: '1px solid rgba(124,77,255,0.1)',
                    }}
                  >
                    {currentDate}
                  </Typography>
                </Box>
              )}
              <MessageBubble
                message={msg}
                isMine={msg.senderId === currentUserId}
              />
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          bgcolor: 'rgba(14,14,26,0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={4}
          disabled={sending}
          sx={borderlessInputSx}
        />
        <IconButton
          onClick={handleSend}
          disabled={!text.trim() || sending}
          sx={{
            width: 40,
            height: 40,
            ...(text.trim()
              ? {
                  background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
                  color: 'white',
                  boxShadow: '0 2px 12px rgba(124,77,255,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6a3de8, #e8356f)',
                  },
                }
              : { color: '#5555a0' }),
          }}
        >
          <Send fontSize="small" />
        </IconButton>
      </Box>
    </>
  );
};

export default ChatWindow;
